import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerMeta, guardarMeta, obtenerEstrategias, obtenerRecordatorios, obtenerDatosMetas } from '../services/metasService';
import { armarResumen, coachDe } from '../utils/metasResumen';
import { DIAS_SEMANA, mesDe, nombreMes, fraseAnimo, acumulado, redondear } from '../utils/metas';
import { mensajeAmigable } from '../utils/errores';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import CampoNumero from './CampoNumero';
import MetasEstrategias from './MetasEstrategias';
import MetasPendientes from './MetasPendientes';
import MetasPlanHoy from './MetasPlanHoy';
import MetasFinanzas from './MetasFinanzas';
import MetasSemana from './MetasSemana';

const ACENTO = 'var(--color-primario,#DB2777)';
const VIOLETA = '#6D28D9';
const TONO = {
  bien: { fondo: '#ECFDF5', borde: '#A7F3D0', texto: '#065F46' },
  atencion: { fondo: '#FFFBEB', borde: '#FDE68A', texto: '#92400E' },
  alerta: { fondo: '#FEF2F2', borde: '#FECACA', texto: '#991B1B' },
  idea: { fondo: '#F5F3FF', borde: '#DDD6FE', texto: '#5B21B6' },
};
const ESTADO = {
  empezando: { txt: 'Empezando el mes', color: VIOLETA, fondo: '#EDE9FE' },
  adelantada: { txt: '🚀 Vas adelantada', color: '#047857', fondo: '#D1FAE5' },
  en_ritmo: { txt: '👍 Vas en ritmo', color: '#0369A1', fondo: '#E0F2FE' },
  atrasada: { txt: '⏰ Vas atrasada', color: '#B91C1C', fondo: '#FEE2E2' },
  cumplida: { txt: '🏆 ¡Meta cumplida!', color: '#B45309', fondo: '#FEF3C7' },
};

const tarjeta = { backgroundColor: 'white', borderRadius: '16px', padding: '16px', border: '1px solid #EEE9F8', boxShadow: '0 1px 3px rgba(60,30,120,.05)' };

// Gráfica: lo vendido acumulado contra la línea de la meta (lo que deberías llevar cada día).
function GraficaAvance({ puntos, meta, dinero }) {
  const [activo, setActivo] = useState(null);
  const W = 640; const H = 200; const izq = 50; const der = 10; const arr = 10; const abj = 22;
  const max = Math.max(meta, ...puntos.map((p) => p.real || 0)) * 1.05 || 1;
  const x = (i) => izq + (i / Math.max(1, puntos.length - 1)) * (W - izq - der);
  const y = (v) => arr + (H - arr - abj) * (1 - v / max);
  const ideal = puntos.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.ideal)}`).join(' ');
  const reales = puntos.filter((p) => p.real !== null);
  const real = reales.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.real)}`).join(' ');
  const area = reales.length ? `${real} L${x(reales.length - 1)},${y(0)} L${x(0)},${y(0)} Z` : '';
  const p = activo != null ? puntos[activo] : null;
  return (
    <div style={{ position: 'relative' }} onMouseLeave={() => setActivo(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Avance del mes contra la meta" style={{ display: 'block' }}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={izq} x2={W - der} y1={y(meta * f)} y2={y(meta * f)} stroke="#F1EEF8" />
            <text x={izq - 6} y={y(meta * f) + 4} textAnchor="end" fontSize="11" fill="#9CA3AF">{dinero(meta * f, true)}</text>
          </g>
        ))}
        <path d={ideal} fill="none" stroke="#C4B5FD" strokeWidth="2" strokeDasharray="5 5" />
        {area && <path d={area} fill={ACENTO} opacity="0.12" />}
        {real && <path d={real} fill="none" stroke={ACENTO} strokeWidth="2.5" strokeLinejoin="round" />}
        {reales.length > 0 && <circle cx={x(reales.length - 1)} cy={y(reales[reales.length - 1].real)} r="5" fill={ACENTO} stroke="white" strokeWidth="2" />}
        {puntos.map((pt, i) => (i % 5 === 0 || i === puntos.length - 1) && (
          <text key={pt.fecha} x={x(i)} y={H - 6} textAnchor="middle" fontSize="11" fill="#9CA3AF">{pt.dia}</text>
        ))}
        {puntos.map((pt, i) => (
          <rect key={`h${pt.fecha}`} x={x(i) - (W / puntos.length) / 2} y={arr} width={W / puntos.length} height={H - arr - abj} fill="transparent"
            onMouseEnter={() => setActivo(i)} onClick={() => setActivo(i)} />
        ))}
      </svg>
      {p && (
        <div style={{ position: 'absolute', top: 0, left: `${(x(activo) / W) * 100}%`, transform: `translateX(${activo > puntos.length / 2 ? '-100%' : '0'})`,
          background: 'white', border: '1px solid #EEE9F8', borderRadius: '10px', padding: '8px 10px', fontSize: '12px', boxShadow: '0 6px 18px rgba(30,20,60,.12)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          <b>Día {p.dia}</b><br />
          {p.real !== null ? <>Llevabas: {dinero(p.real)}<br /></> : null}
          Meta a ese día: {dinero(p.ideal)}
        </div>
      )}
      <div style={{ display: 'flex', gap: '14px', fontSize: '12px', color: '#6B7280', marginTop: '6px', flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: '14px', height: '3px', background: ACENTO, verticalAlign: 'middle', marginRight: '5px' }} />Lo que llevas vendido</span>
        <span><span style={{ display: 'inline-block', width: '14px', borderTop: '2px dashed #C4B5FD', verticalAlign: 'middle', marginRight: '5px' }} />Lo que deberías llevar</span>
      </div>
    </div>
  );
}

// 🎯 Mis metas: planificar el mes (meta, presupuesto, estrategias, pendientes)
// y ver cada día cómo va el negocio, con consejos y ánimo.
export default function MetasView({ onVolver, onIr }) {
  const { negocio } = useAuth();
  const { mostrarToast } = useToast();
  const s = simboloMoneda();
  const dinero = useCallback((n, corto = false) => {
    const v = Number(n || 0);
    if (corto && v >= 1000) return `${s} ${(v / 1000).toLocaleString('es-HN', { maximumFractionDigits: 1 })} mil`;
    return `${s} ${Math.round(v).toLocaleString('es-HN')}`;
  }, [s]);

  const hoy = new Date();
  const [mes, setMes] = useState(mesDe(hoy));
  const esEsteMes = mes === mesDe(hoy);
  // El aviso del viernes abre directo en "Mi semana" (App guarda la pestaña pedida).
  const [pestana, setPestana] = useState(() => {
    try { const p = sessionStorage.getItem('kontrol_metas_pestana'); sessionStorage.removeItem('kontrol_metas_pestana'); return p || 'voy'; } catch { return 'voy'; }
  });
  const [datos, setDatos] = useState(null);
  const [meta, setMeta] = useState(undefined);
  const [estrategias, setEstrategias] = useState([]);
  const [recordatorios, setRecordatorios] = useState([]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState(null);

  const cargar = useCallback(() => {
    setError(null);
    Promise.all([obtenerDatosMetas(mes), obtenerMeta(mes), obtenerEstrategias(mes), obtenerRecordatorios()])
      .then(([d, m, e, r]) => { setDatos(d); setMeta(m); setEstrategias(e); setRecordatorios(r); })
      .catch((e) => setError(mensajeAmigable(e)));
  }, [mes]);
  useEffect(() => { setMeta(undefined); cargar(); }, [cargar]);

  const resumen = useMemo(() => (datos ? armarResumen(datos, meta || null, mes, hoy) : null), [datos, meta, mes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Formulario de la meta (prellenado con lo guardado o lo sugerido).
  useEffect(() => {
    if (!resumen || meta === undefined) return;
    setForm({
      metaVentas: meta ? String(meta.meta_ventas) : (resumen.sugerida ? String(resumen.sugerida.meta) : ''),
      diasVenta: meta?.dias_venta || [1, 2, 3, 4, 5, 6],
      motivo: meta?.motivo || '',
      presupuesto: { ...resumen.presupuestoSugerido, ...(meta?.presupuesto || {}) },
    });
    if (meta === null) setPestana('meta');
  }, [resumen, meta]);

  const guardar = async () => {
    const v = Number(form.metaVentas);
    if (!(v > 0)) { mostrarToast('Escribe cuánto quieres vender este mes', 'error'); return; }
    setGuardando(true);
    try {
      const presupuesto = Object.fromEntries(Object.entries(form.presupuesto).filter(([, x]) => Number(x) > 0).map(([k, x]) => [k, Number(x)]));
      const m = await guardarMeta(mes, { metaVentas: v, diasVenta: form.diasVenta, motivo: form.motivo, presupuesto });
      setMeta(m);
      mostrarToast(meta ? 'Meta actualizada ✅' : '🎯 ¡Meta guardada! Ahora arma tus estrategias', 'exito');
      setPestana(meta ? 'voy' : 'estrategias');
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setGuardando(false); }
  };

  const pendientesHoy = recordatorios.filter((r) => !r.hecho && new Date(r.cuando).toDateString() === hoy.toDateString()).length;
  const coach = resumen ? coachDe(resumen, s, pendientesHoy) : [];
  const avance = resumen?.avance;
  const est = avance ? ESTADO[avance.estado] : null;

  const pill = (activo) => ({
    padding: '8px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
    border: `1px solid ${activo ? ACENTO : '#E5E7EB'}`, backgroundColor: activo ? ACENTO : 'white', color: activo ? 'white' : '#6B7280',
  });

  return (
    <div className="k-page" style={{ maxWidth: '980px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <h2 className="k-page-title">🎯 Mis metas</h2>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mes"
            style={{ padding: '8px 10px', borderRadius: '10px', border: '1px solid #D1D5DB', fontSize: '13px', backgroundColor: 'white', textTransform: 'capitalize' }}>
            <option value={mesDe(hoy)}>Este mes · {nombreMes(mesDe(hoy))}</option>
            <option value={mesDe(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1))}>Próximo mes · {nombreMes(mesDe(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1)))}</option>
          </select>
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>
      <p style={{ margin: '0 0 14px', color: '#6B7280', fontSize: '14px' }}>Tu plan para <b style={{ textTransform: 'capitalize' }}>{nombreMes(mes)}</b>: cuánto quieres vender, cuánto gastar, qué vas a hacer para lograrlo y tus pendientes. Kontrol te acompaña cada día. 💜</p>

      {error && <div style={{ padding: '14px', background: '#FEE2E2', borderRadius: '12px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</div>}
      {!resumen && !error && <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳ Revisando cómo va tu negocio…</div>}

      {resumen && form && (
        <>
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '14px' }}>
            {[['voy', '📈 Cómo voy'], ['semana', '🗓️ Mi semana'], ['meta', '🎯 Mi meta'], ['presupuesto', '💸 Presupuesto'], ['finanzas', '💰 Finanzas'], ['estrategias', `🧭 Estrategias (${estrategias.length})`], ['pendientes', `⏰ Pendientes${pendientesHoy ? ` · ${pendientesHoy} hoy` : ''}`]].map(([k, t]) => (
              <button key={k} onClick={() => setPestana(k)} style={pill(pestana === k)}>{t}</button>
            ))}
          </div>

          {/* ---------- 📈 Cómo voy ---------- */}
          {pestana === 'voy' && (
            !meta ? (
              <div style={{ ...tarjeta, textAlign: 'center', padding: '28px' }}>
                <div style={{ fontSize: '40px' }}>🎯</div>
                <p style={{ fontWeight: 700, fontSize: '16px', margin: '6px 0' }}>Todavía no tienes meta para {nombreMes(mes)}</p>
                <p style={{ color: '#6B7280', margin: '0 0 14px' }}>Ponerte una meta es el primer paso para que tu negocio crezca en serio.</p>
                <button onClick={() => setPestana('meta')} style={{ ...pill(true), padding: '10px 20px' }}>Ponerme una meta</button>
              </div>
            ) : (
              <>
                {esEsteMes && (
                  <MetasPlanHoy resumen={resumen} meta={meta} estrategias={estrategias} setEstrategias={setEstrategias}
                    recordatorios={recordatorios} datos={datos} negocio={negocio}
                    onRecordatorioCreado={(r) => setRecordatorios((x) => [...x, r])} />
                )}
                <div style={{ ...tarjeta, background: 'linear-gradient(135deg,#FDF2F8 0%,#F5F3FF 100%)', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', fontWeight: 600 }}>Llevas vendido en {nombreMes(mes).split(' ')[0]}</p>
                      <p style={{ margin: '2px 0', fontSize: '30px', fontWeight: 800, color: '#1F1A2E' }}>{dinero(avance.vendido)} <span style={{ fontSize: '15px', color: '#9CA3AF', fontWeight: 600 }}>de {dinero(avance.meta)}</span></p>
                    </div>
                    <span style={{ padding: '6px 12px', borderRadius: '999px', fontWeight: 800, fontSize: '13px', color: est.color, background: est.fondo }}>{est.txt}</span>
                  </div>
                  <div style={{ position: 'relative', height: '16px', background: 'white', borderRadius: '999px', margin: '10px 0 6px', overflow: 'hidden' }}
                    role="img" aria-label={`${avance.pct}% de la meta`}>
                    <div style={{ width: `${Math.min(100, avance.pct)}%`, height: '100%', background: `linear-gradient(90deg, ${VIOLETA}, ${ACENTO})`, borderRadius: '999px', transition: 'width .8s ease' }} />
                    {esEsteMes && avance.meta > 0 && <div title="Lo que deberías llevar hoy" style={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.min(100, (avance.esperado / avance.meta) * 100)}%`, width: '2px', background: '#1F1A2E' }} />}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6B7280', flexWrap: 'wrap', gap: '6px' }}>
                    <span><b style={{ color: '#1F1A2E' }}>{avance.pct}%</b> de tu meta</span>
                    {esEsteMes && <span>A hoy deberías llevar {dinero(avance.esperado)}</span>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginTop: '12px' }}>
                    {[
                      ['🗓️ Hoy te toca vender', avance.estado === 'cumplida' ? '¡Ya cumpliste!' : dinero(avance.porDiaRestante), avance.hoyVende ? 'para ir a tiempo' : 'hoy no es día de venta'],
                      ['📆 Por semana', dinero(avance.porSemana), `${form.diasVenta.length} días de venta`],
                      ['🎯 Te falta', dinero(avance.falta), `${avance.restantes} días de venta quedan`],
                      ['💸 Gastos del mes', dinero(resumen.gastosMes), Object.keys(meta.presupuesto || {}).length ? `de ${dinero(Object.values(meta.presupuesto).reduce((x, y) => x + Number(y), 0))} presupuestado` : 'sin presupuesto'],
                    ].map(([t, v, sub]) => (
                      <div key={t} style={{ background: 'white', borderRadius: '12px', padding: '10px 12px' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{t}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '18px', fontWeight: 800 }}>{v}</p>
                        <p style={{ margin: 0, fontSize: '11px', color: '#9CA3AF' }}>{sub}</p>
                      </div>
                    ))}
                  </div>
                  <p style={{ margin: '12px 0 0', fontSize: '14px', color: '#5B21B6', fontWeight: 600 }}>💜 {fraseAnimo(avance.estado, hoy)}</p>
                  {meta.motivo && <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>Recuerda para qué lo haces: <i>“{meta.motivo}”</i></p>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                  <div style={tarjeta}>
                    <p style={{ margin: '0 0 8px', fontWeight: 800 }}>🧠 Cómo vas hoy</p>
                    {coach.length === 0 && <p style={{ margin: 0, color: '#9CA3AF', fontSize: '13px' }}>Todo tranquilo por ahora.</p>}
                    {coach.map((c, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', padding: '9px 10px', borderRadius: '10px', marginBottom: '6px', background: TONO[c.tono].fondo, border: `1px solid ${TONO[c.tono].borde}`, color: TONO[c.tono].texto, fontSize: '13px' }}>
                        <span>{c.icono}</span><span>{c.texto}</span>
                      </div>
                    ))}
                    <button onClick={() => setPestana('estrategias')} className="k-btn-soft" style={{ marginTop: '4px', fontSize: '13px' }}>🧭 Ver qué hacer para mejorar</button>
                  </div>
                  <div style={tarjeta}>
                    <p style={{ margin: '0 0 8px', fontWeight: 800 }}>📈 Tu mes día a día</p>
                    <GraficaAvance puntos={acumulado(resumen.porFecha, mes, avance.meta, form.diasVenta, hoy)} meta={avance.meta} dinero={dinero} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                  <div style={tarjeta}>
                    <p style={{ margin: '0 0 8px', fontWeight: 800 }}>🧭 Tus estrategias</p>
                    {['pendiente', 'en_curso', 'hecha'].map((k) => (
                      <p key={k} style={{ margin: '2px 0', fontSize: '13px' }}>{{ pendiente: '⚪ Por hacer', en_curso: '🟡 En marcha', hecha: '🟢 Hechas' }[k]}: <b>{estrategias.filter((e) => e.estado === k).length}</b></p>
                    ))}
                    <button onClick={() => setPestana('estrategias')} className="k-btn-soft" style={{ marginTop: '6px', fontSize: '13px' }}>Abrir</button>
                  </div>
                  <div style={tarjeta}>
                    <p style={{ margin: '0 0 8px', fontWeight: 800 }}>⏰ Pendientes de hoy</p>
                    {recordatorios.filter((r) => !r.hecho && new Date(r.cuando).toDateString() === hoy.toDateString()).slice(0, 4).map((r) => (
                      <p key={r.id} style={{ margin: '2px 0', fontSize: '13px' }}>• {r.titulo} <span style={{ color: '#9CA3AF' }}>{new Date(r.cuando).toLocaleTimeString('es-HN', { hour: 'numeric', minute: '2-digit' })}</span></p>
                    ))}
                    {!pendientesHoy && <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>Nada para hoy.</p>}
                    <button onClick={() => setPestana('pendientes')} className="k-btn-soft" style={{ marginTop: '6px', fontSize: '13px' }}>Abrir</button>
                  </div>
                </div>
              </>
            )
          )}

          {/* ---------- 🎯 Mi meta ---------- */}
          {pestana === 'meta' && (
            <div style={tarjeta}>
              <p style={{ margin: '0 0 4px', fontWeight: 800, fontSize: '16px' }}>¿Cuánto quieres vender en {nombreMes(mes)}?</p>
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6B7280' }}>
                {resumen.sugerida
                  ? <>En tus últimos meses vendiste en promedio <b>{dinero(resumen.sugerida.promedio)}</b>. Te sugerimos <b>{dinero(resumen.sugerida.meta)}</b> (un 15 % más): un reto alcanzable.</>
                  : <>Como estás empezando, ponte una meta que te rete pero que sea posible. Puedes ajustarla cuando quieras.</>}
              </p>
              <CampoNumero valor={form.metaVentas} onChange={(v) => setForm({ ...form, metaVentas: v })} prefijo={s} decimales={0} min={0}
                estilo={{ width: '100%', maxWidth: '280px', padding: '12px', fontSize: '22px', fontWeight: 800, border: `2px solid ${ACENTO}`, borderRadius: '12px', boxSizing: 'border-box' }} />
              {resumen.sugerida && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {[[1, 'Igual que siempre'], [1.15, 'Sugerida (+15 %)'], [1.3, 'Reto grande (+30 %)']].map(([f, t]) => (
                    <button key={t} type="button" onClick={() => setForm({ ...form, metaVentas: String(redondear(resumen.sugerida.promedio * f)) })} className="k-btn-soft" style={{ fontSize: '12px' }}>
                      {t}: {dinero(redondear(resumen.sugerida.promedio * f))}
                    </button>
                  ))}
                </div>
              )}

              <p style={{ margin: '18px 0 6px', fontWeight: 700 }}>¿Qué días vendes?</p>
              <div style={{ display: 'flex', gap: '6px' }}>
                {DIAS_SEMANA.map((d) => {
                  const on = form.diasVenta.includes(d.n);
                  return (
                    <button key={d.n} type="button" title={d.nombre} onClick={() => setForm({ ...form, diasVenta: on ? form.diasVenta.filter((x) => x !== d.n) : [...form.diasVenta, d.n].sort() })}
                      style={{ width: '40px', height: '40px', borderRadius: '50%', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                        border: `2px solid ${on ? ACENTO : '#E5E7EB'}`, background: on ? ACENTO : 'white', color: on ? 'white' : '#9CA3AF' }}>
                      {d.corto}
                    </button>
                  );
                })}
              </div>
              {Number(form.metaVentas) > 0 && form.diasVenta.length > 0 && (() => {
                const prev = armarResumen(datos, { meta_ventas: Number(form.metaVentas), dias_venta: form.diasVenta, presupuesto: {} }, mes, hoy).avance;
                return (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                    {[['Por día', prev.porDia], ['Por semana', prev.porSemana]].map(([t, v]) => (
                      <span key={t} style={{ background: '#F5F3FF', color: '#5B21B6', padding: '8px 12px', borderRadius: '10px', fontSize: '13px' }}>{t}: <b>{dinero(v)}</b></span>
                    ))}
                    <span style={{ background: '#F5F3FF', color: '#5B21B6', padding: '8px 12px', borderRadius: '10px', fontSize: '13px' }}><b>{prev.diasVentaTotal}</b> días de venta en el mes</span>
                  </div>
                );
              })()}

              <p style={{ margin: '18px 0 6px', fontWeight: 700 }}>¿Para qué quieres lograrlo? <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(opcional, te lo recordaremos)</span></p>
              <input className="k-input" value={form.motivo} maxLength={300} onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                placeholder="Ej: comprar más mercadería para diciembre, ahorrar para mi local…" />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                <button onClick={() => setPestana('presupuesto')} className="k-btn-soft">💸 Seguir con el presupuesto</button>
                <button onClick={guardar} disabled={guardando} style={{ ...pill(true), padding: '10px 20px' }}>{guardando ? 'Guardando…' : '💾 Guardar mi meta'}</button>
              </div>
            </div>
          )}

          {/* ---------- 💸 Presupuesto ---------- */}
          {pestana === 'presupuesto' && (
            <div style={tarjeta}>
              <p style={{ margin: '0 0 4px', fontWeight: 800, fontSize: '16px' }}>¿Cuánto vas a gastar en cada cosa?</p>
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6B7280' }}>Te sugerimos lo que gastaste en promedio los meses pasados. Kontrol te avisa cuando vayas por el 80 % de lo que pusiste.</p>
              {[...new Set([...resumen.categoriasGasto, ...Object.keys(form.presupuesto), ...Object.keys(resumen.gastadoPorCategoria)])].map((c) => {
                const p = Number(form.presupuesto[c] || 0); const g = resumen.gastadoPorCategoria[c] || 0;
                const pct = p > 0 ? Math.round((g / p) * 100) : null;
                const color = pct == null ? '#D1D5DB' : pct > 100 ? '#DC2626' : pct >= 80 ? '#D97706' : '#059669';
                return (
                  <div key={c} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,1.4fr) minmax(120px,1fr) minmax(140px,1.6fr)', gap: '10px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{c}</span>
                    <CampoNumero valor={form.presupuesto[c] ? String(form.presupuesto[c]) : ''} onChange={(v) => setForm({ ...form, presupuesto: { ...form.presupuesto, [c]: v } })}
                      prefijo={s} decimales={0} min={0} placeholder="0"
                      estilo={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                    <div>
                      <div style={{ height: '8px', background: '#F3F4F6', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, pct || 0)}%`, height: '100%', background: color }} />
                      </div>
                      <small style={{ color: '#6B7280' }}>{esEsteMes ? `Gastado: ${dinero(g)}${pct != null ? ` · ${pct}%` : ''}` : 'Se llena cuando empiece el mes'}</small>
                    </div>
                  </div>
                );
              })}
              <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#374151' }}>
                Total presupuestado: <b>{dinero(Object.values(form.presupuesto).reduce((x, y) => x + Number(y || 0), 0))}</b>
                {Number(form.metaVentas) > 0 && <> · {Math.round((Object.values(form.presupuesto).reduce((x, y) => x + Number(y || 0), 0) / Number(form.metaVentas)) * 100)}% de tu meta de ventas</>}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                <button onClick={guardar} disabled={guardando} style={{ ...pill(true), padding: '10px 20px' }}>{guardando ? 'Guardando…' : '💾 Guardar meta y presupuesto'}</button>
              </div>
            </div>
          )}

          {pestana === 'semana' && (
            <MetasSemana datos={datos} resumen={resumen} meta={meta} estrategias={estrategias} recordatorios={recordatorios} dinero={dinero} />
          )}
          {pestana === 'finanzas' && (
            <MetasFinanzas resumen={resumen} meta={meta} datos={datos} dinero={dinero} simbolo={s} mes={mes} onMeta={setMeta} />
          )}
          {pestana === 'estrategias' && (
            <MetasEstrategias mes={mes} meta={meta} resumen={resumen} estrategias={estrategias} setEstrategias={setEstrategias}
              usoIA={datos.usoIA} negocio={negocio} onIr={onIr} onRecordatorioCreado={(r) => setRecordatorios((x) => [...x, r])} />
          )}
          {pestana === 'pendientes' && (
            <MetasPendientes recordatorios={recordatorios} setRecordatorios={setRecordatorios} estrategias={estrategias} />
          )}
        </>
      )}
    </div>
  );
}
